import { ChangeEvent, KeyboardEventHandler, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCookies } from 'react-cookie';
import { useTheme } from '@mui/material/styles';

import { useRootDispatch } from '../../../../../../../store/hooks';
import { updateSitePricingVIPCode } from '../../../../../store/features/discounts';
import { UpdateSitePricingVIPCodeData } from '../../../../../store/features/discounts/types';
import { useVIPCodeCookie } from '../../../../../hooks/discount/useVIPCodeCookie';
import { useGetVIPCodeErrorMessages } from '../../../../../hooks/discount/useGetVIPCodeErrorMessages';

import { PricingIconTypes } from '../../../../../types/enums';
import CircledIcon from '../../../../library/CircledIcon';
import InfoWidgetItemWithPaper from '../../../InfoWidgetList/InfoWidgetItemWithPaper';

import { StyledButton, StyledContentWrapper, StyledTextInput } from './styles';
import { PricingIcons } from '../../../../../helpers/icons/icons';

export interface VIPCodeFormProps {
  code?: string;
  onSuccess?: (vipCode: string) => void;
  onValidateVipCode?: (
    vipCode: string,
    onSuccess: () => void,
    onError: (errorMessage: string) => void,
  ) => void;
}

export default function VIPCodeForm(props: VIPCodeFormProps) {
  const { code = '', onSuccess, onValidateVipCode } = props;
  const theme = useTheme();
  const { t } = useTranslation();
  const [cookies] = useCookies();
  const { setVIPCodeCookie, removeVIPCodeCookie } = useVIPCodeCookie();
  const dispatch = useRootDispatch();
  const [disableButton, setDisableButton] = useState(false);
  const [codeInputValue, setCodeInputValue] = useState(code);
  const [helperText, setHelperText] = useState('');
  const [isError, setIsError] = useState(false);
  const Icon = PricingIcons[PricingIconTypes.percentIcon];
  const { getVIPCodeErrorMessage } = useGetVIPCodeErrorMessages();

  const buttonClickHandler = () => {
    if (!codeInputValue) {
      setIsError(true);
      setDisableButton(false);
      setHelperText(
        t('site.pricing.discount.vip-code.validation.REQUIRED.message'),
      );
      return;
    }

    setDisableButton(true);
    setHelperText('');
    setIsError(false);

    if (onValidateVipCode) {
      const handleError = (errorMessage: string) => {
        setHelperText(errorMessage);
        setIsError(true);
        removeVIPCodeCookie();
      };
      const handleSuccess = () => {
        onSuccess?.(codeInputValue);
        setVIPCodeCookie(codeInputValue);
      };

      onValidateVipCode(codeInputValue, handleSuccess, handleError);
      setDisableButton(false);

      return;
    }

    dispatch(
      updateSitePricingVIPCode({
        basketId: cookies.basket_id,
        code: codeInputValue,
      }),
    ).then((response) => {
      const {
        payload,
        meta: { requestStatus },
      } = response;
      setDisableButton(false);

      if (requestStatus === 'fulfilled') {
        setVIPCodeCookie(codeInputValue);
        onSuccess?.(codeInputValue);
      }

      if (requestStatus !== 'fulfilled') {
        const rejectedPayload = payload as
          | { data: UpdateSitePricingVIPCodeData }
          | undefined;
        const discountData = rejectedPayload?.data?.data;

        setHelperText(
          getVIPCodeErrorMessage({
            status: discountData?.status,
            productNames: discountData?.productNames,
            delegateTypesNames: discountData?.delegateTypesNames,
          }),
        );
        setIsError(true);
      }
    });
  };

  const keyDownHandler: KeyboardEventHandler<HTMLInputElement> = (event) => {
    if (event.code === 'Enter') {
      event.preventDefault();
      buttonClickHandler();
    }
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setCodeInputValue(event.target.value);
  };

  return (
    <InfoWidgetItemWithPaper
      icon={
        <CircledIcon
          backgroundRGBColor={theme.palette.neutral.RGB0}
          opacity={1}
          size="small"
        >
          <Icon sx={{ color: theme.palette.brandPrimary.mainCustom }} />
        </CircledIcon>
      }
      title={
        <StyledContentWrapper>
          <StyledTextInput
            value={codeInputValue}
            onChange={handleInputChange}
            onKeyDown={keyDownHandler}
            placeholder={t(
              'site.pricing.discount.vip-code.form.field.code.label',
            )}
            helperText={helperText}
            error={isError}
            size="small"
          />

          <StyledButton
            size="small"
            variant="outlined"
            onClick={buttonClickHandler}
            disabled={disableButton}
          >
            {t('site.pricing.discount.vip-code.form.button.label')}
          </StyledButton>
        </StyledContentWrapper>
      }
    />
  );
}
